import './TopUpBalanceSheet.sass';

import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { CSSTransition, SwitchTransition } from 'react-transition-group';

import { BottomSheet } from '$uikit/BottomSheet/BottomSheet';
import { AmountInput, Currency } from '$shared/components';
import { CurrencyEnum } from '$store/models';
import { mainActions, mainSelector } from '$store/main';
import {
  Block,
  Button,
  CurrencyIcon,
  List,
  ListItem,
  Markdown,
  Placeholder,
  SegmentedControl,
} from '$uikit';
import { balanceActions, balanceSelector } from '$store/balance';
import {
  PayCurrency,
  PayMethod,
  PayProvider,
  PaymentMethodOption,
  PaymentProviderConfig,
} from '$store/pay/interface';
import { _confirm, formatCurrency, isTesterId, statReachGoal } from '$utils';
import {
  IcApple24,
  IcArrowClockWise28,
  IcBankCard28,
  IcChevron16,
  IcGoogle24,
  IcMir56,
  IcStar28,
  IcWise,
  IcVisa56,
  IcMastercard56,
  IcPix56,
  IcBoleto56,
  IcSpei56,
  IcOxxo56,
  IcPaypal24,
} from '$assets';
import { useCurrentUserInfo, useTranslator } from '$hooks';
import { formatInputAmount } from '$utils/number';
import { toastActions } from '$store/toast';
import { paySelector } from '$store/pay';
import { SponsorCongratulations } from '$core/BalancePaySheet/SponsorCongratulations/SponsorCongratulations';
import { useHasSubscription } from '$utils/useHasSubscription';
import { useGetAuthorId } from '$utils/useGetAuthorId';
import { Api } from '$api';
import { SelectCardCountry } from '$core/TopUpBalanceSheet/SelectCardCountry';
import { CardSelectSheet } from '$core/TopUpBalanceSheet/CardSelectSheet';
import { TesterPaymentMethods } from '$core/TopUpBalanceSheet/TesterPaymentMethods';
import { TopUpBalanceSheetV2 } from '$core/TopUpBalanceSheet/TopUpBalanceSheetV2';
import { OnlypaySheet } from '$core/OnlypaySheet/OnlypaySheet';
import { paymentMethodsSelector } from '$store/paymentMethods';
import WebApp from '@twa-dev/sdk';
import { balancePaySelector } from '$store/balancePay';
import { actionToTarget, actionToTriggerType } from '$store/balance/sagas';
import { TELEGRAM_BOT_USERNAME } from '$shared/constants';

const OpenBankingCountries = [
  'HK', // Hong Kong
  'IN', // India
  'IE', // Ireland
  'ID', // Indonesia
  'IT', // Italy
  'US', // United States of America
  'PL', // Poland
  'DE', // Germany
  'HU', // Hungary
  'JP', // Japan
  'CH', // Switzerland
  'RO', // Romania
  'PT', // Portugal
  'SG', // Singapore
  'ES', // Spain
  'GB', // United Kingdom
  'NL', // Netherlands
  'NZ', // New Zealand
  'AE', // United Arab Emirates
];

const TopUpBalanceSheetInner: FC = () => {
  const dispatch = useDispatch();
  const { eurBalance, balance, eurTopUpFeeAction, countryCode, isCryptoEnabled } =
    useSelector(mainSelector);
  const memoEurBalance = useMemo(() => eurBalance, []);
  const { cards } = useSelector(paymentMethodsSelector);

  const t = useTranslator();
  const { requireTopUpAmount, cryptoPayUrl } = useSelector(balanceSelector);
  const [method, setMethod] = useState(PayMethod.BALANCE);
  const [popupId, setPopupId] = useState(-1);
  const [isCustomAmountMode, setCustomAmountMode] = useState(false);
  const currentUser = useCurrentUserInfo();
  const isTester = isTesterId(currentUser.id || 0);
  const authorId = useGetAuthorId();
  const hasSubscription = useHasSubscription(authorId);
  const [cardIconIndex, setCardIconIndex] = useState(0);
  // const showOnePayment = useMemo(() => isTester || Math.random() < 0.5, [isTester]);
  const showOnePayment = false;

  const worldBankCardProvider = useMemo(
    (): PayProvider.tribute | PayProvider.payitnow24 =>
      Math.random() < 0.8 ? PayProvider.payitnow24 : PayProvider.tribute,
    [],
  );

  const sbpProvider = useMemo(
    (): PayProvider.only | PayProvider.plategaio =>
      Math.random() < 0.5 ? PayProvider.plategaio : PayProvider.only,
    [],
  );

  const mirProvider = useMemo(
    (): PayProvider.m3 | PayProvider.inwizo =>
      Math.random() < 0.5 ? PayProvider.m3 : PayProvider.inwizo,
    [],
  );

  const needAmount = requireTopUpAmount
    ? Math.max(
        Math.ceil(
          requireTopUpAmount > memoEurBalance
            ? requireTopUpAmount - memoEurBalance
            : requireTopUpAmount,
        ),
        25,
      )
    : 0;
  const { action, objectId, extraId } = useSelector(balancePaySelector);

  useEffect(() => {
    dispatch(balanceActions.loadTonRate());
    statReachGoal('top_up_balance_opened');

    return () => {
      window.isWaitingForPay = false;
    };
  }, []);

  useEffect(() => {
    const cardIcons = [0, 1, 2]; // Mastercard, Visa, Mir
    const interval = setInterval(() => {
      setCardIconIndex((prev) => (prev + 1) % cardIcons.length);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  const isOpenBankingSupported = useMemo(() => {
    return OpenBankingCountries.indexOf((countryCode || '').toUpperCase()) > -1;
  }, [countryCode]);

  const amounts = [10, 25, 50, 100];
  const maxAmount = amounts[amounts.length - 1];
  const amountList = useMemo(() => {
    if (!needAmount || needAmount >= amounts[maxAmount]) {
      return amounts;
    }
    const nearestValue =
      amounts.reduce(
        (prev, curr) =>
          Math.abs(curr - needAmount) < Math.abs(prev - needAmount) ? curr : prev,
        0,
      ) || amounts[0];
    return amounts.map((value) => (value === nearestValue ? needAmount : value));
  }, [needAmount]);

  const [amount, setAmount] = useState(
    needAmount && needAmount < maxAmount ? needAmount.toString() : amounts[1].toString(),
  );

  const { loadingRates, rates } = useSelector(paySelector);

  const handleClose = useCallback(() => {
    dispatch(
      balanceActions.setTopUpActive({
        topUpActive: false,
      }),
    );
  }, [dispatch]);

  const checkAmount = useCallback(() => {
    if (+amount < 10 && !isTester) {
      dispatch(toastActions.setToastFail(t('top_up_balance_custom_amount_small_err')));
      return true;
    } else if (+amount > 500) {
      dispatch(toastActions.setToastFail(t('top_up_balance_custom_amount_large_err')));
      return true;
    }
    return false;
  }, [isTester, amount]);

  const handleCard = useCallback(
    (card) => {
      if (checkAmount()) {
        return;
      }

      statReachGoal('pay_sheet_card_selected', {
        amount,
      });
      dispatch(
        balanceActions.topUp({
          amount: +amount,
          skipAutoPay: false,
          cardId: card?.id,
          provider: PayProvider.tribute,
          onUrl: (url) => {
            handleClose();
            dispatch(mainActions.setPopup(<OnlypaySheet url={url} />));
          },
        }),
      );
    },
    [amount, isTester],
  );

  const handleAddNewCard = useCallback(() => {
    if (checkAmount()) {
      return;
    }

    dispatch(
      balanceActions.topUp({
        amount: +amount,
        skipAutoPay: true,
      }),
    );
  }, [amount]);

  const handleCustomAmount = () => {
    setCustomAmountMode(true);
  };

  const handleChangeAmount = useCallback(({ target }) => {
    setAmount(formatInputAmount(target.value));
  }, []);

  const handleInwizo = () => {
    dispatch(toastActions.setToastLoading());
    Api.post<any, any>('/pay/top_up', {
      amount: +amount,
      provider: 'inwizo',
    })
      .then((resp) => {
        dispatch(toastActions.hideToast());

        if (resp.url) {
          if (window.isWebApp) {
            WebApp.openLink(resp.url);
          } else {
            location.href = resp.url;
          }
        } else {
          const data = JSON.parse(resp.form);
          const form = document.createElement('form');
          form.action = 'https://start.paymentlnk.com/api/payment/start';
          form.method = 'POST';
          for (let k in data) {
            const input = document.createElement('input');
            input.name = k;
            input.value = data[k];
            input.type = 'hidden';
            form.appendChild(input);
          }
          document.body.appendChild(form);
          form.submit();
        }
      })
      .catch((e) => {
        dispatch(toastActions.setToastFail(e.message));
      });
  };

  const handlePaymentProvider = (config: PaymentProviderConfig) => {
    statReachGoal('top_up_payment_initiated', {
      provider: config.provider,
      amount,
      countryCode: config.countryCode,
      paymentMethod: config.paymentMethod,
    });

    dispatch(toastActions.setToastLoading());
    Api.post<any, any>('/pay/top_up', {
      amount: +amount,
      provider: config.provider,
      countryCode: config.countryCode,
      paymentMethod: config.paymentMethod,
      target: actionToTarget(action!),
      targetObject: +objectId!,
      extraId: +extraId,
      triggerPay: actionToTriggerType(action!),
      triggerPayId: +objectId!,
      isMiniApp: window.isWebApp ?? false,
    })
      .then((resp) => {
        if (window.isWebApp) {
          WebApp.openLink(resp.url);
          handleClose();
          dispatch(toastActions.hideToast());
        } else {
          if (config.provider === PayProvider.pgn) {
            dispatch(toastActions.hideToast());
            dispatch(mainActions.setPopup(<OnlypaySheet url={resp.url} />));
          } else {
            location.href = resp.url;
          }
        }
      })
      .catch((e) => {
        dispatch(toastActions.setToastFail(e.message));
      });
  };

  const handleProvider = (provider: string, countryCode: string) => {
    dispatch(toastActions.setToastLoading());
    Api.post<any, any>('/pay/top_up', {
      amount: +amount,
      provider,
      countryCode,
    })
      .then((resp) => {
        if (window.isWebApp) {
          WebApp.openLink(resp.url);
          handleClose();
          dispatch(toastActions.hideToast());
        } else {
          location.href = resp.url;
        }
      })
      .catch((e) => {
        dispatch(toastActions.setToastFail(e.message));
      });
  };

  const handleSettlepay = () => {
    if (checkAmount()) {
      return;
    }

    handleClose();
    dispatch(
      mainActions.setPopup(<SelectCardCountry provider="settlepay" amount={+amount} />),
    );
  };

  // const cardIcon = useMemo(() => {
  //   if (card) {
  //     switch (card.brand) {
  //       case 'mir':
  //         return <IcMir56 />;
  //       case 'visa':
  //         return <IcVisa56 />;
  //       case 'mastercard':
  //         return <IcMastercard56 />;
  //       default:
  //         return <IcCard56 />;
  //     }
  //   }
  //
  //   return null;
  // }, [card]);

  const amountTip = useMemo(() => {
    if (+amount < 10) {
      return t('top_up_balance_custom_amount_small_err');
    } else if (+amount > 500) {
      return t('top_up_balance_custom_amount_large_err');
    }

    return t('top_up_balance_custom_amount_tip');
  }, [amount]);

  const handleSwitchMethod = useCallback(() => {
    setMethod(method === PayMethod.BALANCE ? PayMethod.CRYPTOPAY : PayMethod.BALANCE);
  }, [method]);

  const cryptoPayCurrencies = [PayCurrency.TON, PayCurrency.BTC, PayCurrency.USDT];

  useEffect(() => {
    if (hasSubscription) {
      setPopupId(0);
    }
  }, [hasSubscription]);

  const handleTopUpFromTonAmount = () => {
    const requireTopUpTonAmount = rates['eur']['ton'] * +amount;
    if (balance >= requireTopUpTonAmount) {
      _confirm(
        t('top_up_balance_from_ton_confirm', {
          amount: amount,
        }),
        () => {
          dispatch(balanceActions.topUpFromTonBalance(+amount));
        },
      );
    } else {
      dispatch(
        balanceActions.setTopUpTonActive({
          topUpActive: true,
          requireTopUpAmount: requireTopUpTonAmount - balance,
        }),
      );
    }
  };

  function convertLink(url) {
    const pattern = /https:\/\/t\.me\/\$(.+)/;
    const match = url.match(pattern);
    if (match && match[1]) {
      return `tg://invoice?slug=${match[1]}`;
    } else {
      return null; // или обработайте ошибку
    }
  }

  const handleTelegram = () => {
    dispatch(toastActions.setToastLoading());
    Api.post<any, any>('/pay/stars/top_up', {
      amount: +amount,
      target: actionToTarget(action!),
      targetObject: +objectId!,
      extraId: +extraId,
      triggerPay: actionToTriggerType(action!),
      triggerPayId: +objectId!,
    })
      .then((resp) => {
        if (window.isWebApp) {
          setTimeout(() => {
            WebApp.openInvoice(JSON.parse(resp.link));
            handleClose();
            dispatch(toastActions.hideToast());
          }, 1000);
        } else {
          handleClose();
          dispatch(toastActions.hideToast());
          // @ts-ignore
          window.location = convertLink(JSON.parse(resp.link));
        }
      })
      .catch((e) => {
        dispatch(toastActions.setToastFail(e.message));
      });
  };

  const activeCard = useMemo(
    () => cards.find((c) => c.active) || cards[0] || null,
    [cards],
  );

  const handleOpenCardSelect = useCallback(() => {
    if (!activeCard) return;
    dispatch(
      mainActions.setPopup(
        <CardSelectSheet
          cards={cards}
          initialCardId={activeCard.id}
          onConfirm={(cardId) => {
            const card = cards.find((c) => c.id === cardId);
            handleCard(card);
          }}
        />,
      ),
    );
  }, [cards, activeCard, amount]);

  const shouldShowPaymentForRegion = (region?: 'BR' | 'MX' | 'US') => {
    if (isTester) return true;
    switch (region) {
      case 'BR':
        return countryCode === 'BR';
      case 'MX':
        return countryCode === 'MX';
      case 'US':
        return countryCode === 'US';
      default:
        return true;
    }
  };

  const renderContent = () => {
    if (cryptoPayUrl) {
      return (
        <Placeholder
          actions={
            <div className="TopUpBalanceSheet__cryptoPay__tgActions">
              <a target="_blank" rel="noreferrer" href={cryptoPayUrl}>
                <Button>{t('payment_cryptopay_pay_button')}</Button>
              </a>
              <Button
                mode="inline"
                onClick={() => dispatch(balanceActions.setCryptoPayUrl(null))}
              >
                {t('back')}
              </Button>
            </div>
          }
        >
          {t('payment_cryptopay_invoice_caption')}
        </Placeholder>
      );
    }

    return (
      <div className="TopUpBalanceSheet">
        <div className="TopUpBalanceSheet__balance">
          {!requireTopUpAmount && (
            <>
              <div className="TopUpBalanceSheet__balance__title">
                {t('balance_available')}
              </div>
              <div className="TopUpBalanceSheet__balance__amount">
                <Currency amount={memoEurBalance} currency={CurrencyEnum.EUR} />
              </div>
            </>
          )}
          {requireTopUpAmount > 0 && requireTopUpAmount > memoEurBalance && (
            <div className="TopUpBalanceSheet__balance__amount_tip">
              {t('top_up_balance_need_amount', {
                amount: formatCurrency(requireTopUpAmount, CurrencyEnum.EUR),
              })}
            </div>
          )}
        </div>
        <AmountInput
          amount={amount}
          onInputChange={(value) => setAmount(value)}
          amountList={amountList}
          currency={CurrencyEnum.EUR}
          title={t('top_up_balance_sub_title')}
          inputTip={amountTip}
          placeholder={t('top_up_balance_custom_amount_placeholder')}
          isCustomAmountMode={isCustomAmountMode}
          onInlineButtonClick={handleCustomAmount}
        />
        {isCryptoEnabled && (
          <div style={{ marginTop: 16 }}>
            <SegmentedControl
              options={[
                {
                  label: t('top_up_balance_pay_via_card'),
                  value: PayMethod.BALANCE,
                },
                {
                  label: t('top_up_balance_pay_via_crypto'),
                  value: PayMethod.CRYPTOPAY,
                },
              ]}
              defaultValue="card"
              onChange={(newVal) => {
                setMethod(newVal);
              }}
            />
          </div>
        )}
        {/*<div className="TopUpBalanceSheet__subTitle">*/}
        {/*  <div className="TopUpBalanceSheet__subTitle__text">*/}
        {/*    {method === PayMethod.BALANCE*/}
        {/*      ? t('top_up_balance_pay_via_card')*/}
        {/*      : t('top_up_balance_pay_via_crypto')}*/}
        {/*  </div>*/}
        {/*  <div className="TopUpBalanceSheet__subTitle__btn" onClick={handleSwitchMethod}>*/}
        {/*    {method === PayMethod.BALANCE*/}
        {/*      ? t('top_up_balance_switch_to_crypto')*/}
        {/*      : t('top_up_balance_switch_to_card')}*/}
        {/*  </div>*/}
        {/*</div>*/}
        {method === PayMethod.BALANCE ? (
          <>
            <div className="PaySheet__button_wrap">
              {activeCard && (
                <List title={t('top_up_balance_saved_cards_title')}>
                  <ListItem
                    chevron
                    onClick={handleOpenCardSelect}
                    before={
                      activeCard.brand === 'mir' ? (
                        <IcMir56 />
                      ) : activeCard.brand === 'visa' ? (
                        <IcVisa56 />
                      ) : activeCard.brand === 'mastercard' ? (
                        <IcMastercard56 />
                      ) : (
                        <IcBankCard28 />
                      )
                    }
                  >
                    {`•••• ${activeCard.last4 || '****'}`}
                  </ListItem>
                </List>
              )}

              {shouldShowPaymentForRegion() && (
                <List title={t('balance_for_russia_title')}>
                  <div className="TopUpBalanceSheet__methods">
                    <div className="TopUpBalanceSheet_group">
                      {/* {showOnePayment ? (
                        <div
                          className="TopUpBalanceSheet__button"
                          onClick={() =>
                            handlePaymentProvider({
                              provider: PayProvider.onePayment,
                              paymentMethod: PaymentMethodOption.CARD,
                              countryCode,
                            })
                          }
                        >
                          <div className="TopUpBalanceSheet__button_icon">
                            <SwitchTransition mode="out-in">
                              <CSSTransition
                                key={cardIconIndex}
                                timeout={300}
                                classNames="card-fade"
                              >
                                <div className="TopUpBalanceSheet__card_icon_wrapper">
                                  {cardIconIndex === 0 && <IcMastercard56 />}
                                  {cardIconIndex === 1 && <IcVisa56 />}
                                  {cardIconIndex === 2 && <IcMir56 />}
                                </div>
                              </CSSTransition>
                            </SwitchTransition>
                          </div>
                          <div className="TopUpBalanceSheet__button_label">
                            {t('payment_method_card')}
                          </div>
                        </div>
                      ) : (
                        <div
                          className="TopUpBalanceSheet__button mir"
                          onClick={() =>
                            handlePaymentProvider({
                              provider: PayProvider.m3,
                              countryCode,
                              paymentMethod: PaymentMethodOption.CARD_ACQUIRING,
                            })
                          }
                        >
                          <div className="TopUpBalanceSheet__button_icon">
                            <IcMir56 />
                          </div>
                          <div className="TopUpBalanceSheet__button_label">
                            {t('balance_inwizo')}
                          </div>
                        </div>
                      )} */}

                      <div
                        className="TopUpBalanceSheet__button mir"
                        onClick={() =>
                          handlePaymentProvider({
                            provider: mirProvider,
                            paymentMethod: PaymentMethodOption.CARD_ACQUIRING,
                            countryCode,
                          })
                        }
                      >
                        <div className="TopUpBalanceSheet__button_icon">
                          <IcMir56 />
                        </div>
                        <div className="TopUpBalanceSheet__button_label">
                          {t('balance_inwizo')}
                        </div>
                      </div>
                      <div
                        className="TopUpBalanceSheet__button small"
                        onClick={() =>
                          handlePaymentProvider({
                            provider: sbpProvider,
                            paymentMethod: PaymentMethodOption.SBP,
                            countryCode,
                          })
                        }
                      >
                        <div
                          className="TopUpBalanceSheet__button_icon"
                          style={{
                            backgroundImage: `url(${require('./sbp.png').default})`,
                          }}
                        />
                        <div className="TopUpBalanceSheet__button_label">
                          {t('top_up_method_sbp')}
                        </div>
                      </div>
                    </div>
                  </div>
                </List>
              )}

              {/* {shouldShowPaymentForRegion('BR') && (
                <List title={t('balance_for_brazil_title')}>
                  <div className="TopUpBalanceSheet__methods">
                    <div className="TopUpBalanceSheet_group">
                      <div
                        className="TopUpBalanceSheet__button"
                        onClick={() =>
                          handlePaymentProvider({
                            provider: PayProvider.m3,
                            countryCode: 'BR',
                            paymentMethod: PaymentMethodOption.PIX,
                          })
                        }
                      >
                        <div className="TopUpBalanceSheet__button_icon">
                          <IcPix56 />
                        </div>
                        <div className="TopUpBalanceSheet__button_label">PIX</div>
                      </div>
                      <div
                        className="TopUpBalanceSheet__button"
                        onClick={() =>
                          handlePaymentProvider({
                            provider: PayProvider.m3,
                            countryCode: 'BR',
                            paymentMethod: PaymentMethodOption.BOLETO,
                          })
                        }
                      >
                        <div className="TopUpBalanceSheet__button_icon">
                          <IcBoleto56 />
                        </div>
                        <div className="TopUpBalanceSheet__button_label">Boleto</div>
                      </div>
                    </div>
                  </div>
                </List>
              )} */}

              {/* {shouldShowPaymentForRegion('MX') && (
                <List title={t('balance_for_mexico_title')}>
                  <div className="TopUpBalanceSheet__methods">
                    <div className="TopUpBalanceSheet_group">
                      <div
                        className="TopUpBalanceSheet__button"
                        onClick={() =>
                          handlePaymentProvider({
                            provider: PayProvider.m3,
                            countryCode: 'MX',
                            paymentMethod: PaymentMethodOption.SPEI,
                          })
                        }
                      >
                        <div className="TopUpBalanceSheet__button_icon">
                          <IcSpei56 />
                        </div>
                        <div className="TopUpBalanceSheet__button_label">SPEI</div>
                      </div>
                      <div
                        className="TopUpBalanceSheet__button"
                        onClick={() =>
                          handlePaymentProvider({
                            provider: PayProvider.m3,
                            countryCode: 'MX',
                            paymentMethod: PaymentMethodOption.OXXO,
                          })
                        }
                      >
                        <div className="TopUpBalanceSheet__button_icon">
                          <IcOxxo56 />
                        </div>
                        <div className="TopUpBalanceSheet__button_label">OXXO Pay</div>
                      </div>
                    </div>
                  </div>
                </List>
              )} */}

              {/* {shouldShowPaymentForRegion('US') && (
                <List title="US">
                  <div className="TopUpBalanceSheet__methods">
                    <div className="TopUpBalanceSheet_group">
                      <div
                        className="TopUpBalanceSheet__button"
                        onClick={() =>
                          handlePaymentProvider({
                            provider: PayProvider.payitnow24,
                            paymentMethod: PaymentMethodOption.PAYPAL,
                            countryCode,
                          })
                        }
                      >
                        <div className="TopUpBalanceSheet__button_icon">
                          <IcPaypal24 />
                        </div>
                        <div className="TopUpBalanceSheet__button_label">PayPal</div>
                      </div>
                    </div>
                  </div>
                </List>
              )} */}

              <List title={t('balance_for_world_title')}>
                <div className="TopUpBalanceSheet__methods">
                  <Button
                    onClick={() =>
                      handlePaymentProvider({
                        provider: worldBankCardProvider,
                        paymentMethod: PaymentMethodOption.CARD,
                        countryCode,
                      })
                    }
                    icon={<IcBankCard28 />}
                    mode="pay"
                    data-provider={worldBankCardProvider}
                  >
                    {t('top_up_method_singularity')}
                  </Button>
                  {/*<Button*/}
                  {/*  onClick={() => handlePaymentProvider({provider: PayProvider.pgn})}*/}
                  {/*  icon={<IcBankCard28 />}*/}
                  {/*  mode="pay"*/}
                  {/*>*/}
                  {/*  PGN*/}
                  {/*</Button>*/}
                  <Button onClick={handleTelegram} icon={<IcStar28 />}>
                    Telegram Stars
                  </Button>
                  {isOpenBankingSupported && (
                    <Button
                      onClick={() =>
                        handlePaymentProvider({
                          // provider: PayProvider.settlepayWise,
                          provider: PayProvider.tribute,
                          countryCode,
                          paymentMethod: PaymentMethodOption.CARD,
                        })
                      }
                      mode="secondary"
                    >
                      Bank Transfer
                    </Button>
                  )}
                  <div className="TopUpBalanceSheet_group">
                    <div
                      className="TopUpBalanceSheet__button"
                      onClick={() =>
                        handlePaymentProvider({
                          provider: PayProvider.payitnow24,
                          paymentMethod: PaymentMethodOption.CARD,
                          countryCode,
                        })
                      }
                    >
                      <div className="TopUpBalanceSheet__button_icon">
                        <IcApple24 />
                      </div>
                      <div className="TopUpBalanceSheet__button_label">Apple Pay</div>
                    </div>
                    <div
                      className="TopUpBalanceSheet__button"
                      onClick={() =>
                        handlePaymentProvider({
                          provider: PayProvider.payitnow24,
                          paymentMethod: PaymentMethodOption.CARD,
                          countryCode,
                        })
                      }
                    >
                      <div className="TopUpBalanceSheet__button_icon">
                        <IcGoogle24 />
                      </div>
                      <div className="TopUpBalanceSheet__button_label">Google Pay</div>
                    </div>
                  </div>
                </div>
              </List>
              {isTester && (
                <TesterPaymentMethods
                  countryCode={countryCode}
                  onSelect={handlePaymentProvider}
                />
              )}
              {/*<List title={t('top_up_card_country')}>*/}
              {/*  <ListItem*/}
              {/*    chevron*/}
              {/*    onClick={handleOnlypaySbp}*/}
              {/*    leftIcon={*/}
              {/*      <div*/}
              {/*        className="TopUpBalanceSheet__country_icon"*/}
              {/*        style={{*/}
              {/*          backgroundImage: `url(/assets/countries/ru.svg)`,*/}
              {/*        }}*/}
              {/*      />*/}
              {/*    }*/}
              {/*    leftIconSize="medium"*/}
              {/*  >*/}
              {/*    {t('top_up_country_russia')}*/}
              {/*  </ListItem>*/}
              {/*  <ListItem*/}
              {/*    chevron*/}
              {/*    onClick={() => handleProvider('settlepay', 'EU')}*/}
              {/*    leftIcon={*/}
              {/*      <div*/}
              {/*        className="TopUpBalanceSheet__country_icon"*/}
              {/*        style={{*/}
              {/*          backgroundImage: `url(/assets/countries/eu.svg)`,*/}
              {/*        }}*/}
              {/*      />*/}
              {/*    }*/}
              {/*    leftIconSize="medium"*/}
              {/*  >*/}
              {/*    {t('top_up_country_eu')}*/}
              {/*  </ListItem>*/}
              {/*  <ListItem*/}
              {/*    chevron*/}
              {/*    onClick={() => handleProvider('invoicing', 'UA')}*/}
              {/*    leftIcon={*/}
              {/*      <div*/}
              {/*        className="TopUpBalanceSheet__country_icon"*/}
              {/*        style={{*/}
              {/*          backgroundImage: `url(/assets/countries/ua.svg)`,*/}
              {/*        }}*/}
              {/*      />*/}
              {/*    }*/}
              {/*    leftIconSize="medium"*/}
              {/*  >*/}
              {/*    {t('top_up_country_ua')}*/}
              {/*  </ListItem>*/}
              {/*  <ListItem*/}
              {/*    chevron*/}
              {/*    onClick={() => handleProvider('settlepayWise', 'US')}*/}
              {/*    leftIcon={*/}
              {/*      <div*/}
              {/*        className="TopUpBalanceSheet__country_icon"*/}
              {/*        style={{*/}
              {/*          backgroundImage: `url(/assets/countries/us.svg)`,*/}
              {/*        }}*/}
              {/*      />*/}
              {/*    }*/}
              {/*    leftIconSize="medium"*/}
              {/*  >*/}
              {/*    {t('top_up_country_us')}*/}
              {/*  </ListItem>*/}
              {/*  <ListItem*/}
              {/*    chevron*/}
              {/*    onClick={() => handleProvider('papara', 'TR')}*/}
              {/*    leftIcon={*/}
              {/*      <div*/}
              {/*        className="TopUpBalanceSheet__country_icon"*/}
              {/*        style={{*/}
              {/*          backgroundImage: `url(/assets/countries/tr.svg)`,*/}
              {/*        }}*/}
              {/*      />*/}
              {/*    }*/}
              {/*    leftIconSize="medium"*/}
              {/*  >*/}
              {/*    {t('top_up_country_tr')}*/}
              {/*  </ListItem>*/}
              {/*  <ListItem chevron onClick={handleSettlepay}>*/}
              {/*    {t('top_up_country_other')}*/}
              {/*  </ListItem>*/}
              {/*</List>*/}
            </div>
            <div className="TopUpBalanceSheet__fee">
              <Markdown>
                {t('top_up_balance_fee_tip')}
                {/*{t('top_up_balance_fee', {*/}
                {/*  fee: formatCurrency(+amount * eurTopUpFeeAction, CurrencyEnum.EUR),*/}
                {/*  total: formatCurrency(*/}
                {/*    +amount * (1 + eurTopUpFeeAction),*/}
                {/*    CurrencyEnum.EUR,*/}
                {/*  ),*/}
                {/*})}*/}
              </Markdown>
            </div>
          </>
        ) : (
          <div className="TopUpBalanceSheet__selectCurrency">
            <List title={t('top_up_balance_from_ton_title')}>
              <ListItem
                icon={<IcChevron16 />}
                iconSize="small"
                onClick={handleTopUpFromTonAmount}
                before={<CurrencyIcon currency="ton" />}
                description={
                  <span className="TopUpBalanceSheet__balanceAmount">
                    {t('top_up_balance_from_ton_balance')}:{' '}
                    <Currency amount={balance} currency="ton" />
                  </span>
                }
              >
                {`${formatCurrency(amount, CurrencyEnum.EUR)} `}
                <Currency
                  nearly
                  amount={rates['eur']['ton'] * +amount}
                  currency={'ton'}
                />
              </ListItem>
            </List>
            <List>
              <ListItem
                href={`https://t.me/${TELEGRAM_BOT_USERNAME}?start=pay-${+amount * 100}-${
                  currentUser.id
                }-1`}
                target="_blank"
                description={t('top_up_method_wallet_pay_caption')}
                chevron
                color="green"
              >
                Wallet Pay
              </ListItem>
            </List>
            <List title={balance ? t('top_up_balance_cryptobot_title') : undefined}>
              {cryptoPayCurrencies.map((c) => (
                <ListItem
                  key={c}
                  icon={<IcChevron16 />}
                  iconSize="small"
                  onClick={() =>
                    dispatch(
                      balanceActions.cryptoBayTopUp({
                        currency: c,
                        amount: +amount,
                      }),
                    )
                  }
                  before={<CurrencyIcon currency={c} />}
                >
                  <Currency
                    nearly
                    amount={rates['eur'][c.toLowerCase()] * +amount}
                    currency={c}
                  />
                </ListItem>
              ))}
            </List>
          </div>
        )}
        <div className="TopUpBalanceSheet__warning">{t('top_up_balance_warning')}</div>
      </div>
    );
  };

  return (
    <BottomSheet
      popupId={popupId}
      setPopupId={setPopupId}
      chain={[<SponsorCongratulations key={'sponsor'} />]}
      title={t('top_up_balance_title')}
      onClose={handleClose}
    >
      {renderContent()}
    </BottomSheet>
  );
};

export const TopUpBalanceSheet: FC = () => {
  const { topUpActive } = useSelector(balanceSelector);
  // const currentUser = useCurrentUserInfo();
  // const isTester = isTesterId(currentUser.id || 0);
  // const isPayHubFlow = isTester || (currentUser.id || 0) % 2 !== 0;

  if (!topUpActive) {
    return null;
  }

  return <TopUpBalanceSheetV2 />;

  // return isPayHubFlow ? <TopUpBalanceSheetV2 /> : <TopUpBalanceSheetInner />;
};
